import React from "react";
import { HyroHeader, HyroTable } from "@hyro-components";
import { useHyroTableContainer } from "./HyroTableContainer.hook";
import BulkActionsPanel from "src/components/collections/bulk-actions/BulkActionsPanel";
import { PAGINATION_MODES } from "@hyro-components/hyro-table/HyroTable.consts";

const EMPTY_ARRAY = [];

const HyroTableContainer = ({
  columns = EMPTY_ARRAY,
  data = EMPTY_ARRAY,
  header = {
    title: "",
    titleVariant: "h6",
    titleAdornment: null,
    subtitle: "",
    showMainSection: true,
    showDownloadButton: true,
    showSearchBar: true,
    searchQueryParam: "",
    searchFields: undefined,
    enableFilter: true,
    infoTooltipText: "",
    isReadOnly: false,
    children: null
  },
  download = {
    fileName: "",
    fileData: [],
    fileHeaders: [],
    getCSVRows: undefined
  },
  filters = {
    initialFilters: [],
    disableFilters: false,
    filterQueryParam: ""
  },
  refresh = {
    showRefreshButton: false,
    autoRefreshInterval: 30000,
    handleRefresh: () => {},
    isRefreshing: false,
    snapshotKey: undefined
  },
  table = {
    alwaysShowScroll: true,
    disableHover: false,
    overrideMaxRowHeight: false,
    showInlineAddRow: false,
    minHeight: undefined,
    autoNavigateToLastPage: false,
    columnStyle: {},
    enableBulkEdit: false,
    sx: {},
    dataGridProps: {},
    selectedColumn: undefined,
    stretchHeight: false,
    loading: false,
    disableColumnSorting: false,
    truncateCellText: true,
    rowCount: undefined,
    onFiltersChange: () => {}
  },
  pagination = {
    showPagination: false,
    paginationMode: PAGINATION_MODES.CLIENT,
    pageSizeOptions: undefined
  },
  bulkActions = {
    active: false,
    selectedRows: [],
    setSelectedRows: () => {},
    getBulkActionEditCell: () => {},
    handleBulkRowsDelete: () => {},
    handleBulkRowsDuplicate: () => {},
    focusedField: null,
    setFocusedField: () => {},
    enableAddDeleteRows: false
  },
  rowActions,
  onRowClick,
  onRowUpdate,
  onAddInlineRow,
  onRowSelectionModelChange,
  testId,
  tableRef,
  noRowsText,
  noResultsText,
  noResultsDescription,
  handlePaginationParamsChange
}) => {
  const {
    searchText,
    setSearchText,
    handleSearch,
    filters: activeFilters,
    setFilters,
    rows,
    isFilteringOn,
    setRowLengthChanged,
    paginationModel,
    sortModel,
    onPaginationModelChange,
    onSortModelChange,
    renewSnapshot
  } = useHyroTableContainer({
    data,
    filters: filters.initialFilters,
    enableBulkEdit: table.enableBulkEdit,
    selectedRows: bulkActions.selectedRows,
    setSelectedRows: bulkActions.setSelectedRows,
    paginationMode: pagination.paginationMode,
    handlePaginationParamsChange,
    snapshotKey: refresh.snapshotKey,
    onFiltersChange: table.onFiltersChange,
    searchQueryParam: header.searchQueryParam,
    searchFields: header.searchFields
  });

  return (
    <>
      {bulkActions.active && (
        <BulkActionsPanel
          setSelectedRows={bulkActions.setSelectedRows}
          selectedRows={bulkActions.selectedRows}
          getBulkActionEditCell={bulkActions.getBulkActionEditCell}
          handleBulkRowsDelete={bulkActions.handleBulkRowsDelete}
          handleBulkRowsDuplicate={bulkActions.handleBulkRowsDuplicate}
          focusedField={bulkActions.focusedField}
          setFocusedField={bulkActions.setFocusedField}
          enableAddDeleteRows={bulkActions.enableAddDeleteRows}
          columns={columns}
          tableRef={tableRef}
          setSearchText={setSearchText}
          setRowLengthChanged={setRowLengthChanged}
        />
      )}

      <HyroHeader
        testId={testId}
        title={header.title}
        titleVariant={header.titleVariant}
        titleAdornment={header.titleAdornment}
        subtitle={header.subtitle}
        rowsLength={pagination.paginationMode === PAGINATION_MODES.SERVER ? table.rowCount : rows?.length}
        isReadOnly={header.isReadOnly}
        showMainSection={header.showMainSection}
        showDownloadButton={header.showDownloadButton}
        showSearchBar={header.showSearchBar}
        enableFilter={header.enableFilter}
        disableSearch={table.loading}
        downloadFileName={download.fileName}
        downloadFileData={download.getCSVRows ? () => download.getCSVRows(rows, columns) : download.fileData}
        downloadFileHeaders={download.fileHeaders}
        searchText={searchText}
        handleSearch={handleSearch}
        searchQueryParam={header.searchQueryParam}
        filters={activeFilters}
        filterQueryParam={filters.filterQueryParam}
        disableFilters={filters.disableFilters || table.loading}
        isBulkEditActive={bulkActions.active}
        setFilters={setFilters}
        infoTooltipText={header.infoTooltipText}
        refreshButtonProps={{
          ...refresh,
          handleRefresh: pagination.paginationMode === PAGINATION_MODES.SERVER ? renewSnapshot : refresh.handleRefresh,
          disabled: table.loading
        }}
      >
        {header.children}
      </HyroHeader>

      <HyroTable
        columns={columns}
        rows={pagination.paginationMode === PAGINATION_MODES.SERVER ? data : rows}
        rowActions={rowActions}
        onRowClick={onRowClick}
        onRowUpdate={onRowUpdate}
        showPagination={pagination.showPagination}
        alwaysShowScroll={table.alwaysShowScroll}
        noRowsText={noRowsText}
        noResultsText={noResultsText}
        noResultsDescription={noResultsDescription}
        disableHover={table.disableHover}
        overrideMaxRowHeight={table.overrideMaxRowHeight}
        testId={testId}
        onAddInlineRow={onAddInlineRow}
        showInlineAddRow={table.showInlineAddRow}
        minHeight={table.minHeight}
        autoNavigateToLastPage={table.autoNavigateToLastPage}
        columnStyle={table.columnStyle}
        enableBulkEdit={table.enableBulkEdit}
        sx={table.sx}
        dataGridProps={table.dataGridProps}
        selectedColumn={table.selectedColumn}
        stretchHeight={table.stretchHeight}
        loading={table.loading}
        disableColumnSorting={table.disableColumnSorting}
        isFilteringOn={isFilteringOn}
        truncateCellText={table.truncateCellText}
        onRowSelectionModelChange={onRowSelectionModelChange}
        ref={tableRef}
        paginationMode={pagination.paginationMode}
        pageSizeOptions={pagination.pageSizeOptions}
        rowCount={table.rowCount}
        onPaginationModelChange={onPaginationModelChange}
        paginationModel={paginationModel}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
      />
    </>
  );
};

export default HyroTableContainer;
